import { useGetEvents } from "@/api/admin/useGetEvents";
import { EventDto, ReservationRequestDto } from "@/api/generated/generatedTypes";
import { useState } from "react";

export interface UseChangeRequestModalParams {
  eventsResponse: ReturnType<typeof useGetEvents>["data"];
  onRequestChanged: () => void;
}

export interface UseChangeRequestModalReturn {
  isOpen: boolean;
  reservation: ReservationRequestDto | null;
  currentEvent: EventDto | null;
  openModal: (requestId: number, eventId: number) => void;
  closeModal: () => void;
  handleCancel: (eventId: number) => void;
}

/**
 * Hook to manage change request modal state
 * Handles opening/closing modal and tracking current reservation/event
 */
export function useChangeRequestModal({
  eventsResponse,
  onRequestChanged,
}: UseChangeRequestModalParams): UseChangeRequestModalReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [reservation, setReservation] = useState<ReservationRequestDto | null>(null);
  const [currentEvent, setCurrentEvent] = useState<EventDto | null>(null);

  const openModal = (requestId: number, eventId: number) => {
    const event = eventsResponse?.events.find((e) => e.id === eventId);
    if (!event) return;

    const foundReservation = event.reservationRequests.find((r) => r.id === requestId);
    if (!foundReservation) return;

    setReservation(foundReservation);
    setCurrentEvent(event);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setReservation(null);
    setCurrentEvent(null);
  };

  const handleCancel = (eventId: number) => {
    onRequestChanged();
  };

  return {
    isOpen,
    reservation,
    currentEvent,
    openModal,
    closeModal,
    handleCancel,
  };
}
